import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { UNSAFE_NavigationContext, UNSAFE_LocationContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { driver, Driver } from 'driver.js';
import './driver-theme.css';
import { useAuthContext } from 'modules/auth';
import * as AuthApi from 'modules/auth/api';

export interface TourStepConfig {
  path: string;
  element: string;
  titleKey: string;
  descKey: string;
  defaultTitle: string;
  defaultDesc: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Targets live inside the sample test; dropped when the org has none. */
  needsDemoTest?: boolean;
}

function waitForElement(selector: string, timeout = 2000): Promise<Element | null> {
  return new Promise((resolve) => {
    const el = document.querySelector(selector);
    if (el) return resolve(el);

    const observer = new MutationObserver(() => {
      const found = document.querySelector(selector);
      if (found) {
        observer.disconnect();
        resolve(found);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(document.querySelector(selector));
    }, timeout);
  });
}

const FallbackNavigationContext = React.createContext<any>(null);
const FallbackLocationContext = React.createContext<any>(null);
const SafeNavContext = (UNSAFE_NavigationContext as unknown as React.Context<any>) || FallbackNavigationContext;
const SafeLocContext = (UNSAFE_LocationContext as unknown as React.Context<any>) || FallbackLocationContext;


/**
 * The tour auto-starts only on the dashboard. Elsewhere (a template preview or
 * the create screen opened from onboarding's "Your picks") it would navigate
 * the user away from what they just chose.
 */
export function isTourAutoStartPath(pathname: string): boolean {
  return pathname === '/app' || pathname === '/app/' || pathname.startsWith('/app/analytics');
}

export function useProductTour() {
  const { t } = useTranslation();
  const navContext = useContext(SafeNavContext) as
    | { navigator?: { push: (to: string) => void } }
    | undefined;
  const locContext = useContext(SafeLocContext) as
    | { location?: { pathname: string } }
    | undefined;

  const pathname = locContext?.location?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '');

  const navigate = useCallback(
    (to: string) => {
      if (navContext?.navigator) {
        navContext.navigator.push(to);
      } else if (typeof window !== 'undefined') {
        window.location.href = to;
      }
    },
    [navContext],
  );

  const { state, methods } = useAuthContext();

  const orgId = state.organization?.id;
  const hasDemoWorkspace = Boolean((state.organization as any)?.hasDemoWorkspace);
  const demoTestId = (state.organization as any)?.demoTestId as string | undefined;

  const storageKey = orgId ? `examing_tour_completed_${orgId}` : 'examing_tour_completed';
  const [isRunning, setIsRunning] = useState(false);
  const driverRef = useRef<Driver | null>(null);
  const currentStepRef = useRef(0);

  const getSteps = useCallback((): TourStepConfig[] => {
    const testPath = demoTestId ? `/app/tests/${demoTestId}` : '/app/tests';

    const steps: TourStepConfig[] = [
      {
        path: '/app/analytics',
        element: '#demo-workspace-banner, #dashboard-overview-card',
        titleKey: 'tour.step_dashboard_title',
        descKey: 'tour.step_dashboard_desc',
        defaultTitle: '✨ 1-Qadam: Boshqaruv Paneli & Tahlillar',
        defaultDesc:
          'Barcha test topshirishlar, o‘rtacha ballar, o‘quvchilar va xodimlar faolligi hamda monitoring ma\'lumotlarini real vaqtda kuzatib boring.',
        position: 'bottom',
      },
      {
        path: '/app/subjects',
        element: '[data-tour="demo-subject-card"], #subjects-page-container',
        titleKey: 'tour.step_subjects_title',
        descKey: 'tour.step_subjects_desc',
        defaultTitle: '📚 2-Qadam: Fanlar va Yo‘nalishlar',
        defaultDesc:
          'Fanlar sizning barcha savollaringiz va testlaringizni fan, sinf yoki ko‘nikmalar bo‘yicha tizimlashtiradi. Bu yerda yangi fanlar qo‘shing yoki mavjudlarini tahrirlang.',
        position: 'bottom',
      },
      {
        path: '/app/questions',
        element: '[data-tour="question-bank-header"], #questions-header',
        titleKey: 'tour.step_bank_title',
        descKey: 'tour.step_bank_desc',
        defaultTitle: '❓ 3-Qadam: Savollar Banki',
        defaultDesc:
          'Barcha turdagi savollarni (test, moslashtirish, ochiq yozma, matematik LaTeX formulalar) yarating, tushuntirishlar va to‘g‘ri javoblarni biriktiring.',
        position: 'bottom',
      },
      {
        path: '/app/documents',
        element: '[data-tour="documents-page"], #documents-page-container',
        titleKey: 'tour.step_ai_docs_title',
        descKey: 'tour.step_ai_docs_desc',
        defaultTitle: '🤖 4-Qadam: AI Hujjatlar & Avtomatik Generatsiya',
        defaultDesc:
          'Darsliklar, PDF yoki DOCX fayllarni yuklang. Sun\'iy intellekt hujjatdan avtomatik ravishda tayyor savollar va tushuntirishlarni bir necha soniyada yaratadi.',
        position: 'bottom',
      },
      {
        path: '/app/tests',
        element: '[data-tour="tests-page"], #tests-page-container',
        titleKey: 'tour.step_tests_title',
        descKey: 'tour.step_tests_desc',
        defaultTitle: '📝 5-Qadam: Testlar Boshqaruvi',
        defaultDesc:
          'Barcha yaratilgan testlaringiz: qoralamalar, faol va yakunlangan imtihonlar, ishtirokchilar soni va o‘rtacha ko‘rsatkichlar bir joyda.',
        position: 'bottom',
      },
      {
        path: testPath,
        needsDemoTest: true,
        element: '[data-tour="test-details-section"], #test-details-section, #test-overview-card',
        titleKey: 'tour.step_config_title',
        descKey: 'tour.step_config_desc',
        defaultTitle: '🛡️ 6-Qadam: Test Strukturasi & Anti-Cheating',
        defaultDesc:
          'Bo‘limlar, vaqt chegarasi, o‘tish bali va ko‘chirishga qarshi qat\'iy nazorat (to‘liq ekran, tab almashtirishni cheklash, proctoring).',
        position: 'bottom',
      },
      {
        path: testPath,
        needsDemoTest: true,
        element: '[data-tour="test-share-panel"], #test-share-panel, [data-tour="test-access-share-box"]',
        titleKey: 'tour.step_share_title',
        descKey: 'tour.step_share_desc',
        defaultTitle: '🚀 7-Qadam: Testni Ulashish & O‘tkazish',
        defaultDesc:
          'Ommaviy havola yoki maxsus kod orqali testni ulashing. Nomzodlar to‘g‘ridan-to‘g‘ri havola orqali test topshirishi mumkin.',
        position: 'bottom',
      },
      {
        path: '/app/teams',
        element: '[data-tour="teams-page"], #teams-page-container',
        titleKey: 'tour.step_teams_title',
        descKey: 'tour.step_teams_desc',
        defaultTitle: '👥 8-Qadam: Guruhlar & Sinflar',
        defaultDesc:
          'O‘quvchilar yoki xodimlarni guruhlarga (sinf, bo‘lim) birlashtiring, bitta tugma bilan umumiy test tayinlang va guruh statistikasini solishtiring.',
        position: 'bottom',
      },
      {
        path: '/app/grading',
        element: '[data-tour="grading-page"], #grading-page-container',
        titleKey: 'tour.step_grading_title',
        descKey: 'tour.step_grading_desc',
        defaultTitle: '✍️ 9-Qadam: Tekshirish & Baholash (Grading)',
        defaultDesc:
          'Oddiy testlar avtomatik baholanadi. Yozma insho va ochiq savollarni esa qulay navbatda AI ko‘magida yoki qo‘lda tezkor tekshiring.',
        position: 'bottom',
      },
      {
        path: '/app/analytics',
        element: '[data-tour="header-profile"], #header-profile-btn, button[aria-label="Account"]',
        titleKey: 'tour.step_profile_title',
        descKey: 'tour.step_profile_desc',
        defaultTitle: '⚙️ 10-Qadam: Jamoa & Tashkilot Sozlamalari',
        defaultDesc:
          'Hamkasblaringizni taklif qiling, rollar bering (Admin, Muallif, Nazoratchi), profilingizni boshqaring va Payme/Click orqali to‘lovlarni qiling. Sayohat yakunlandi! 🎉',
        position: 'bottom',
      },
    ];
    // The structure and share steps point inside a sample test; without one
    // (orgs that skipped sample data) their targets don't exist, so skip them.
    return demoTestId ? steps : steps.filter((step) => !step.needsDemoTest);
  }, [demoTestId]);

  const markCompleted = useCallback(async () => {
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      // ignore localStorage errors
    }
    setIsRunning(false);

    if (state.user && !state.user.tourDismissed) {
      try {
        await AuthApi.UpdateProfile({ tourDismissed: true });
        if (methods?.setUser) {
          methods.setUser((prev) => (prev ? { ...prev, tourDismissed: true } : prev));
        }
      } catch {
        // non-blocking
      }
    }
  }, [storageKey, state.user, methods]);

  const goToStep = useCallback(
    async (stepIndex: number) => {
      const steps = getSteps();
      if (stepIndex < 0 || stepIndex >= steps.length) {
        if (driverRef.current) {
          driverRef.current.destroy();
        }
        markCompleted();
        return;
      }

      currentStepRef.current = stepIndex;
      const step = steps[stepIndex];

      // Navigate if route is different
      if (pathname !== step.path) {
        navigate(step.path);
      }

      // Wait for element to mount on target page
      await waitForElement(step.element, 2000);

      const targetEl = document.querySelector(step.element) || document.body;

      if (!driverRef.current) {
        driverRef.current = driver({
          showProgress: true,
          animate: true,
          popoverClass: 'examing-tour-popover',
          progressText: `${stepIndex + 1} / ${steps.length}`,
          nextBtnText: stepIndex === steps.length - 1 ? t('finish', 'Tugatish') : t('next', 'Keyingisi'),
          prevBtnText: t('back', 'Oldingisi'),
          onDestroyStarted: () => {
            markCompleted();
            driverRef.current?.destroy();
          },
        });
      }

      driverRef.current.highlight({
        element: targetEl as HTMLElement,
        popover: {
          title: t(step.titleKey, step.defaultTitle),
          description: t(step.descKey, step.defaultDesc),
          side: step.position || 'bottom',
          align: 'start',
          showButtons: ['next', 'previous', 'close'],
          closeBtnLabel: t('close', 'Yopish'),
          nextBtnText: stepIndex === steps.length - 1 ? t('finish', 'Tugatish 🎉') : t('next', 'Keyingisi →'),
          prevBtnText: stepIndex === 0 ? t('skip', 'O‘tkazib yuborish') : t('back', '← Oldingisi'),
          onNextClick: () => {
            if (stepIndex === steps.length - 1) {
              driverRef.current?.destroy();
              markCompleted();
            } else {
              goToStep(stepIndex + 1);
            }
          },
          onPrevClick: () => {
            if (stepIndex === 0) {
              driverRef.current?.destroy();
              markCompleted();
            } else {
              goToStep(stepIndex - 1);
            }
          },
        },
      });
    },
    [getSteps, pathname, markCompleted, navigate, t],
  );

  const startTour = useCallback(() => {
    setIsRunning(true);
    goToStep(0);
  }, [goToStep]);

  const resetTour = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    startTour();
  }, [storageKey, startTour]);

  // Auto-start tour on first visit to /app if tour hasn't been completed
  useEffect(() => {
    if (!state.isFetched || !isTourAutoStartPath(pathname)) return;
    const isDismissedInDb = Boolean(state.user?.tourDismissed);
    const isCompleted = isDismissedInDb || localStorage.getItem(storageKey) === 'true';
    if (!isCompleted && !isRunning) {
      // Small timeout to allow initial dashboard render
      const timer = setTimeout(() => {
        startTour();
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [state.isFetched, state.user?.tourDismissed, storageKey, isRunning, startTour, pathname]);

  return {
    startTour,
    resetTour,
    skipTour: markCompleted,
    isRunning,
    hasDemoWorkspace,
  };
}
